import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTolgee } from '@tolgee/react';
import useAbandonedBusinessToken from '../../hooks/abandoned-business/useAbandonedBusinessToken';
import useDishSelection from '../../hooks/abandoned-business/useDishSelection';
import useAbandonedBusinessSubmit from '../../hooks/abandoned-business/useAbandonedBusinessSubmit';
import AbandonedBusinessLoadingScreen from './AbandonedBusinessLoadingScreen';
import AbandonedBusinessErrorScreen from './AbandonedBusinessErrorScreen';
import AbandonedBusinessSuccessScreen from './AbandonedBusinessSuccessScreen';
import AlreadyConfirmedScreen from './AlreadyConfirmedScreen';
import AbandonedBusinessContent from './AbandonedBusinessContent';

const normalizePreferredLanguage = (value: string | undefined) => {
  if (!value) return 'fr';
  const normalized = value.toLowerCase().split(/[-_]/)[0];
  return ['en', 'fr', 'de'].includes(normalized) ? normalized : 'fr';
};

const AbandonedBusinessPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const tokenState = useAbandonedBusinessToken(token);
  const dishSelection = useDishSelection();
  const { submit, state: submitState } = useAbandonedBusinessSubmit(
    token || '',
  );
  const { changeLanguage } = useTolgee();

  const preferredLanguage =
    tokenState.status === 'success' ||
    tokenState.status === 'already_confirmed' ||
    tokenState.status === 'expired'
      ? tokenState.data.userPreferredLanguage
      : undefined;

  useEffect(() => {
    if (!preferredLanguage) return;
    changeLanguage(normalizePreferredLanguage(preferredLanguage));
  }, [preferredLanguage, changeLanguage]);

  // During route transitions `useParams()` can be briefly empty; keep showing the loading screen.
  if (token === undefined) return <AbandonedBusinessLoadingScreen />;

  if (submitState.status === 'success') {
    return (
      <AbandonedBusinessSuccessScreen
        didNotTakeAnything={dishSelection.selection.didNotTakeAnything}
      />
    );
  }

  if (tokenState.status === 'loading')
    return <AbandonedBusinessLoadingScreen />;
  if (tokenState.status === 'error')
    return <AbandonedBusinessErrorScreen variant='invalid' />;
  if (tokenState.status === 'expired')
    return <AbandonedBusinessErrorScreen variant='expired' />;
  if (tokenState.status === 'already_confirmed') {
    const { data } = tokenState;
    return (
      <AlreadyConfirmedScreen
        userName={data?.firstName || ''}
        fridgeName={data?.fridgeName || ''}
        openedAt={data?.openedAt || ''}
      />
    );
  }

  return (
    <AbandonedBusinessContent
      data={tokenState.data}
      dishSelection={dishSelection}
      onSubmit={() => submit(dishSelection.selection)}
      isSubmitting={submitState.status === 'submitting'}
      submitError={
        submitState.status === 'error' ? submitState.message : undefined
      }
    />
  );
};

export default AbandonedBusinessPage;
